import React from 'react';
import './Alerts.css';

function liveTrackingStatusModifier(status) {
  const s = String(status ?? '').toLowerCase().trim();
  if (['active', 'online', 'running', 'live'].includes(s)) return 'live-tracking-alert--active';
  if (['inactive', 'offline', 'unknown', 'stopped', 'idle'].includes(s)) return 'live-tracking-alert--inactive';
  if (s === 'sos' || s === 'emergency' || s === 'alert') return 'live-tracking-alert--sos';
  return 'live-tracking-alert--default';
}

const mapPinIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
    />
  </svg>
);

const Alerts = ({ alerts, onDismiss, onDismissAll }) => {
  const toastAlerts = alerts.filter((alert) => alert.type !== 'sos_emergency');

  if (toastAlerts.length === 0) {
    return null;
  }

  const getAlertStyles = (type, alert) => {
    switch (type) {
      case 'live_tracking': {
        const mod = liveTrackingStatusModifier(alert?.deviceStatus);
        return {
          wrapperExtra: `live-tracking-alert ${mod}`,
          bg: 'bg-slate-50',
          border: 'border-transparent',
          text: 'text-slate-800',
          iconColor: 'text-blue-600',
          icon: mapPinIcon,
        };
      }
      case 'error':
        return {
          wrapperExtra: '',
          bg: 'bg-red-50',
          border: 'border-red-100',
          text: 'text-red-800',
          iconColor: 'text-red-500',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      case 'success':
        return {
          wrapperExtra: '',
          bg: 'bg-green-50',
          border: 'border-green-100',
          text: 'text-green-800',
          iconColor: 'text-green-500',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          ),
        };
      case 'info':
        return {
          wrapperExtra: '',
          bg: 'bg-blue-50',
          border: 'border-blue-100',
          text: 'text-blue-800',
          iconColor: 'text-blue-500',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      case 'warning':
      default:
        return {
          wrapperExtra: '',
          bg: 'bg-yellow-50',
          border: 'border-yellow-100',
          text: 'text-yellow-800',
          iconColor: 'text-yellow-500',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
        };
    }
  };

  return (
    <div className="fixed top-24 right-5 z-[2000] w-full max-w-sm flex flex-col gap-3 pointer-events-none">
      {toastAlerts.length > 1 && (
        <div className="flex justify-end pointer-events-auto animate-slideInRight">
          <button
            type="button"
            onClick={onDismissAll}
            className="bg-white text-gray-500 hover:text-red-600 text-xs font-medium py-1.5 px-3 rounded-full shadow-sm border border-gray-200 transition-all duration-200 flex items-center gap-1 hover:shadow-md"
          >
            <span>Clear all</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {toastAlerts.map((alert) => {
        const style = getAlertStyles(alert.type, alert);
        return (
          <div
            key={alert.id}
            className={`${style.wrapperExtra} ${style.bg} ${style.border} border shadow-lg rounded-xl p-4 flex items-start gap-3 animate-slideInRight pointer-events-auto backdrop-blur-sm bg-opacity-95 transition-all duration-300 hover:translate-x-[-4px]`}
          >
            <div className={`flex-shrink-0 mt-0.5 ${style.iconColor}`}>{style.icon}</div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${style.text} leading-snug break-words`}>{alert.message}</p>
            </div>
            <button
              type="button"
              onClick={() => onDismiss(alert.id)}
              className={`flex-shrink-0 ml-2 ${style.text} opacity-60 hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-black/5`}
              aria-label="Dismiss notification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default Alerts;
